import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';

import { FEATURE_FLAGS, useFeatureFlagState } from '../../hooks/useFeatureFlag';

// User's explicit revert choice.
const OVERRIDE_KEY = 'tpn:useDarkTheme';
// Last *resolved* decision — seeds the first paint to avoid a light→dark flash.
const RESOLVED_KEY = 'tpn:useDarkTheme:resolved';

interface ThemeVersionState {
  // Whether the viewer is allowed to use (and toggle) the dark theme.
  canToggle: boolean;
  useDark: boolean;
  setUseDark: (value: boolean) => void;
}

const ThemeVersionContext = createContext<ThemeVersionState>({
  canToggle: false,
  useDark: false,
  setUseDark: () => undefined,
});

export const ThemeVersionProvider = ThemeVersionContext.Provider;

/**
 * Resolves which theme to use. Permitted users (via the `can_view_dark_theme`
 * flag) default to the dark theme and may revert to the original (persisted in
 * localStorage) via the widget; everyone else gets the original theme.
 */
export function useThemeVersionValue(): ThemeVersionState {
  const { enabled: permitted, loaded } = useFeatureFlagState(FEATURE_FLAGS.DARK_THEME);

  const [override, setOverride] = useState<boolean | null>(() => {
    const stored = localStorage.getItem(OVERRIDE_KEY);
    return stored === null ? null : stored === 'true';
  });

  // Seed the first paint from last session's resolved value so reloads render
  // the right theme immediately instead of light-then-dark.
  const [displayed, setDisplayed] = useState<boolean>(() => localStorage.getItem(RESOLVED_KEY) === 'true');

  // Permitted users default to the dark theme and may revert; others are pinned
  // to the original.
  const liveUseDark = permitted ? override ?? true : false;

  // Only reconcile once the flags have actually loaded — otherwise the
  // fail-closed loading state (permitted=false) would flash the original theme.
  useEffect(() => {
    if (!loaded) return;
    setDisplayed(liveUseDark);
    localStorage.setItem(RESOLVED_KEY, String(liveUseDark));
  }, [loaded, liveUseDark]);

  const setUseDark = useCallback((value: boolean) => {
    localStorage.setItem(OVERRIDE_KEY, String(value));
    localStorage.setItem(RESOLVED_KEY, String(value));
    setOverride(value);
    setDisplayed(value);
  }, []);

  return { canToggle: permitted, useDark: displayed, setUseDark };
}

/**
 * Small floating widget letting permitted users flip between the dark theme and
 * the original. Self-hides for everyone else.
 */
export function ThemeRevertWidget() {
  const { canToggle, useDark, setUseDark } = useContext(ThemeVersionContext);
  if (!canToggle) return null;
  return (
    <Paper
      elevation={3}
      sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1300, px: 1, py: 0.5 }}
    >
      <Button size="small" onClick={() => setUseDark(!useDark)}>
        {useDark ? 'Use original theme' : 'Use new theme'}
      </Button>
    </Paper>
  );
}
